/**
 * TransformationsStep - Step 4 of view creation wizard
 *
 * Advanced transformations: WHERE, GROUP BY, HAVING, JOINs (optional)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { JoinBuilder } from './JoinBuilder';
import { usePreviewColumns } from '@/hooks/useViews';
import type { ViewFormData } from '@/pages/ViewCreatePage';
import type { WhereCondition, AggregateColumn, HavingCondition, JoinConfig } from '@/types/views';

interface TransformationsStepProps {
  formData: ViewFormData;
  updateFormData: (updates: Partial<ViewFormData>) => void;
  currentViewId?: string; // For edit mode, to exclude from JOIN selections
}

// Filter operators mapping to backend enum
const FILTER_OPERATORS = [
  { value: '=', label: '= (Equals)' },
  { value: '!=', label: '!= (Not Equals)' },
  { value: '>', label: '> (Greater Than)' },
  { value: '<', label: '< (Less Than)' },
  { value: '>=', label: '>= (Greater Than or Equal)' },
  { value: '<=', label: '<= (Less Than or Equal)' },
  { value: 'LIKE', label: 'LIKE (Pattern Match)' },
  { value: 'ILIKE', label: 'ILIKE (Case-Insensitive Pattern)' },
  { value: 'IN', label: 'IN (In List)' },
  { value: 'NOT IN', label: 'NOT IN (Not In List)' },
  { value: 'BETWEEN', label: 'BETWEEN (Range)' },
  { value: 'IS NULL', label: 'IS NULL' },
  { value: 'IS NOT NULL', label: 'IS NOT NULL' },
] as const;

// Aggregate functions
const AGGREGATE_FUNCTIONS = [
  { value: 'COUNT', label: 'COUNT' },
  { value: 'SUM', label: 'SUM' },
  { value: 'AVG', label: 'AVG' },
  { value: 'MIN', label: 'MIN' },
  { value: 'MAX', label: 'MAX' },
  { value: 'COUNT_DISTINCT', label: 'COUNT DISTINCT' },
] as const;

// Join types - removed, now handled by JoinBuilder component

export const TransformationsStep: React.FC<TransformationsStepProps> = ({
  formData,
  updateFormData,
  currentViewId,
}) => {
  const [whereOpen, setWhereOpen] = useState(false);
  const [groupByOpen, setGroupByOpen] = useState(false);
  const [havingOpen, setHavingOpen] = useState(false);
  const [joinsOpen, setJoinsOpen] = useState(false);

  // ============================================================================
  // Column Preview — fetch available columns for pick lists
  // ============================================================================

  const previewColumns = usePreviewColumns();
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [columnFetchError, setColumnFetchError] = useState(false);
  const fetchRequestRef = useRef(0);

  /** Stable key for join targets so the effect only re-fires when targets actually change. */
  const joinTargetIds = useMemo(
    () => formData.joins.map((j) => j.target_view_id || j.target_dataset_id || '').join(','),
    [formData.joins]
  );

  /**
   * Reset column state synchronously (as a guarded render-time adjustment,
   * not an effect) whenever the rules/rule_logic/joinTargetIds trio changes
   * and there are no rules — mirrors the previous values in state so the
   * reset only fires on an actual change, converging in one extra render.
   * Matches the early-return branch the fetch effect below used to run
   * synchronously.
   */
  const [prevStepRules, setPrevStepRules] = useState(formData.rules);
  const [prevStepRuleLogic, setPrevStepRuleLogic] = useState(formData.rule_logic);
  const [prevStepJoinTargetIds, setPrevStepJoinTargetIds] = useState(joinTargetIds);
  if (
    formData.rules !== prevStepRules ||
    formData.rule_logic !== prevStepRuleLogic ||
    joinTargetIds !== prevStepJoinTargetIds
  ) {
    setPrevStepRules(formData.rules);
    setPrevStepRuleLogic(formData.rule_logic);
    setPrevStepJoinTargetIds(joinTargetIds);
    if (!formData.rules?.length) {
      setAvailableColumns([]);
      setColumnFetchError(false);
    }
  }

  useEffect(() => {
    if (!formData.rules?.length) {
      return;
    }

    const requestId = ++fetchRequestRef.current;

    const fetchColumns = async () => {
      try {
        setColumnFetchError(false);
        const joinsWithTargets = formData.joins
          .filter((j) => j.target_view_id || j.target_dataset_id)
          .map(({ on_conditions: _conds, ...rest }) => ({ ...rest, on_conditions: [] as never[] }));
        const result = await previewColumns.mutateAsync({
          rules: formData.rules,
          rule_logic: formData.rule_logic ?? 'AND',
          joins: joinsWithTargets.length > 0 ? joinsWithTargets : undefined,
        });
        if (fetchRequestRef.current === requestId) {
          const cols = [
            ...(result.source_info.base_columns ?? []),
            ...Object.values(result.source_info.joined_columns ?? {}).flat(),
          ];
          setAvailableColumns([...new Set(cols)]);
        }
      } catch {
        if (fetchRequestRef.current === requestId) {
          setColumnFetchError(true);
          setAvailableColumns([]);
        }
      }
    };

    fetchColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.rules, formData.rule_logic, joinTargetIds]);

  /** Whether column Selects should be disabled. */
  const columnsDisabled =
    previewColumns.isPending || columnFetchError || !formData.rules?.length;

  /** Placeholder text for column Selects. */
  const columnPlaceholder = !formData.rules?.length
    ? 'No data source configured'
    : previewColumns.isPending
      ? 'Loading columns\u2026'
      : columnFetchError
        ? 'Unavailable'
        : 'Select column';

  /**
   * Build option list for a column Select, including a "(saved)" fallback
   * for values no longer present in available columns.
   */
  const buildOptions = (
    columns: string[],
    currentValue: string
  ): { value: string; label: string }[] => {
    const options = columns.map((col) => ({ value: col, label: col }));
    if (currentValue && !columns.includes(currentValue)) {
      options.unshift({ value: currentValue, label: `${currentValue} (saved)` });
    }
    return options;
  };

  // ============================================================================
  // WHERE Conditions
  // ============================================================================

  const addWhereCondition = () => {
    const newCondition: WhereCondition = {
      id: `where-${Date.now()}`,
      column: '',
      operator: '=',
      value: '',
      logic: 'AND',
    };
    updateFormData({
      where_conditions: [...formData.where_conditions, newCondition],
    });
    setWhereOpen(true);
  };

  const updateWhereCondition = (id: string, updates: Partial<WhereCondition>) => {
    updateFormData({
      where_conditions: formData.where_conditions.map((condition) =>
        condition.id === id ? { ...condition, ...updates } : condition
      ),
    });
  };

  const removeWhereCondition = (id: string) => {
    updateFormData({
      where_conditions: formData.where_conditions.filter((condition) => condition.id !== id),
    });
  };

  // ============================================================================
  // GROUP BY Configuration
  // ============================================================================

  const initializeGroupBy = () => {
    if (!formData.group_by) {
      updateFormData({
        group_by: {
          group_by_columns: [],
          aggregate_columns: [],
        },
      });
      setGroupByOpen(true);
    }
  };

  const addGroupColumn = () => {
    if (!formData.group_by) return;
    const newColumns = [...formData.group_by.group_by_columns, ''];
    updateFormData({
      group_by: {
        ...formData.group_by,
        group_by_columns: newColumns,
      },
    });
  };

  const updateGroupColumn = (index: number, value: string) => {
    if (!formData.group_by) return;
    const newColumns = [...formData.group_by.group_by_columns];
    newColumns[index] = value;
    updateFormData({
      group_by: {
        ...formData.group_by,
        group_by_columns: newColumns,
      },
    });
  };

  const removeGroupColumn = (index: number) => {
    if (!formData.group_by) return;
    const newColumns = formData.group_by.group_by_columns.filter((_: string, i: number) => i !== index);
    updateFormData({
      group_by: {
        ...formData.group_by,
        group_by_columns: newColumns,
      },
    });
  };

  const addAggregateColumn = () => {
    if (!formData.group_by) return;
    const newAggregate: AggregateColumn = {
      id: `agg-${Date.now()}`,
      column: '',
      function: 'COUNT',
      column_alias: '',
    };
    updateFormData({
      group_by: {
        ...formData.group_by,
        aggregate_columns: [...formData.group_by.aggregate_columns, newAggregate],
      },
    });
  };

  const updateAggregateColumn = (id: string, updates: Partial<AggregateColumn>) => {
    if (!formData.group_by) return;
    updateFormData({
      group_by: {
        ...formData.group_by,
        aggregate_columns: formData.group_by.aggregate_columns.map((agg: AggregateColumn) =>
          agg.id === id ? { ...agg, ...updates } : agg
        ),
      },
    });
  };

  const removeAggregateColumn = (id: string) => {
    if (!formData.group_by) return;
    updateFormData({
      group_by: {
        ...formData.group_by,
        aggregate_columns: formData.group_by.aggregate_columns.filter((agg: AggregateColumn) => agg.id !== id),
      },
    });
  };

  const clearGroupBy = () => {
    updateFormData({ group_by: null });
    setGroupByOpen(false);
  };

  // ============================================================================
  // HAVING Conditions
  // ============================================================================

  const addHavingCondition = () => {
    const newCondition: HavingCondition = {
      id: `having-${Date.now()}`,
      aggregate_column_alias: '',
      operator: '>',
      value: 0,
      logic: 'AND',
    };
    updateFormData({
      having_conditions: [...formData.having_conditions, newCondition],
    });
    setHavingOpen(true);
  };

  const updateHavingCondition = (id: string, updates: Partial<HavingCondition>) => {
    updateFormData({
      having_conditions: formData.having_conditions.map((condition) =>
        condition.id === id ? { ...condition, ...updates } : condition
      ),
    });
  };

  const removeHavingCondition = (id: string) => {
    updateFormData({
      having_conditions: formData.having_conditions.filter((condition) => condition.id !== id),
    });
  };

  // ============================================================================
  // JOIN Configuration - Handled by JoinBuilder component
  // ============================================================================

  const handleJoinsUpdate = (updatedJoins: JoinConfig[]) => {
    updateFormData({ joins: updatedJoins });
    // Auto-open the section if joins are added
    if (updatedJoins.length > 0 && !joinsOpen) {
      setJoinsOpen(true);
    }
  };

  // Get available aggregate aliases for HAVING conditions
  const getAggregateAliases = (): string[] => {
    if (!formData.group_by) return [];
    return formData.group_by.aggregate_columns
      .filter((agg: AggregateColumn) => agg.column_alias)
      .map((agg: AggregateColumn) => agg.column_alias);
  };

  return (
    <div className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          All transformations on this step are optional. Add WHERE conditions to filter data, GROUP BY to create
          aggregations, HAVING to filter aggregated results, or JOINs to combine multiple views.
        </AlertDescription>
      </Alert>

      {/* WHERE Conditions */}
      <Card className="p-4">
        <Collapsible open={whereOpen} onOpenChange={setWhereOpen}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-lg font-semibold hover:text-primary transition-colors">
                {whereOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                WHERE Conditions
                {formData.where_conditions.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground">
                    ({formData.where_conditions.length})
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <Button onClick={addWhereCondition} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Condition
            </Button>
          </div>

          <CollapsibleContent className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Filter rows before aggregation. Use WHERE to reduce the data set and improve query performance.
            </p>

            {formData.where_conditions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                No WHERE conditions defined. Click "Add Condition" to filter data.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.where_conditions.map((condition, index) => (
                  <div key={condition.id} className="border rounded-lg p-4 space-y-3 bg-muted/50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Condition {index + 1}</span>
                      <Button
                        onClick={() => removeWhereCondition(condition.id)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor={`where-column-${condition.id}`}>Column</Label>
                        <Select
                          value={condition.column || undefined}
                          onValueChange={(value) =>
                            updateWhereCondition(condition.id, { column: value })
                          }
                          disabled={columnsDisabled}
                        >
                          <SelectTrigger id={`where-column-${condition.id}`} className="h-9">
                            <SelectValue placeholder={columnPlaceholder} />
                          </SelectTrigger>
                          <SelectContent>
                            {buildOptions(availableColumns, condition.column).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`where-operator-${condition.id}`}>Operator</Label>
                        <Select
                          value={condition.operator}
                          onValueChange={(value) =>
                            updateWhereCondition(condition.id, { operator: value as WhereCondition['operator'] })
                          }
                        >
                          <SelectTrigger id={`where-operator-${condition.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FILTER_OPERATORS.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`where-value-${condition.id}`}>Value</Label>
                        <Input
                          id={`where-value-${condition.id}`}
                          value={String(condition.value)}
                          onChange={(e) => updateWhereCondition(condition.id, { value: e.target.value })}
                          placeholder="value"
                        />
                      </div>
                    </div>

                    {index < formData.where_conditions.length - 1 && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Logic to next:</Label>
                        <Select
                          value={condition.logic || 'AND'}
                          onValueChange={(value) =>
                            updateWhereCondition(condition.id, { logic: value as 'AND' | 'OR' })
                          }
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">AND</SelectItem>
                            <SelectItem value="OR">OR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* GROUP BY Configuration */}
      <Card className="p-4">
        <Collapsible open={groupByOpen} onOpenChange={setGroupByOpen}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-lg font-semibold hover:text-primary transition-colors">
                {groupByOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                GROUP BY Aggregation
                {formData.group_by && (
                  <span className="text-sm font-normal text-muted-foreground">
                    (
                    {formData.group_by.group_by_columns.length} groups,{' '}
                    {formData.group_by.aggregate_columns.length} aggregates)
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            {!formData.group_by ? (
              <Button onClick={initializeGroupBy} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Enable GROUP BY
              </Button>
            ) : (
              <Button onClick={clearGroupBy} size="sm" variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Remove GROUP BY
              </Button>
            )}
          </div>

          <CollapsibleContent className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Group rows by columns and compute aggregates like SUM, COUNT, AVG, etc.
            </p>

            {formData.group_by && (
              <div className="space-y-6">
                {/* Group By Columns */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Group By Columns</Label>
                    <Button onClick={addGroupColumn} size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Column
                    </Button>
                  </div>

                  {formData.group_by.group_by_columns.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
                      No group columns. Add at least one column to group by.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formData.group_by.group_by_columns.map((column: string, index: number) => (
                        <div key={index} className="flex gap-2">
                          <Select
                            value={column || undefined}
                            onValueChange={(value) => updateGroupColumn(index, value)}
                            disabled={columnsDisabled}
                          >
                            <SelectTrigger className="h-9 flex-1">
                              <SelectValue placeholder={columnPlaceholder} />
                            </SelectTrigger>
                            <SelectContent>
                              {buildOptions(availableColumns, column).map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={() => removeGroupColumn(index)}
                            size="sm"
                            variant="ghost"
                            className="px-3"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Aggregate Columns */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Aggregate Columns</Label>
                    <Button onClick={addAggregateColumn} size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Aggregate
                    </Button>
                  </div>

                  {formData.group_by.aggregate_columns.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
                      No aggregates. Add at least one aggregate function.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.group_by.aggregate_columns.map((agg: AggregateColumn) => (
                        <div key={agg.id} className="border rounded-lg p-3 bg-muted/50">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium">Aggregate</span>
                            <Button
                              onClick={() => removeAggregateColumn(agg.id)}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label htmlFor={`agg-function-${agg.id}`}>Function</Label>
                              <Select
                                value={agg.function}
                                onValueChange={(value) =>
                                  updateAggregateColumn(agg.id, { function: value as AggregateColumn['function'] })
                                }
                              >
                                <SelectTrigger id={`agg-function-${agg.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {AGGREGATE_FUNCTIONS.map((func) => (
                                    <SelectItem key={func.value} value={func.value}>
                                      {func.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor={`agg-column-${agg.id}`}>Column</Label>
                              <Select
                                value={agg.column || (
                                  (agg.function === 'COUNT' || agg.function === 'COUNT_DISTINCT')
                                    ? '__all_columns__' : undefined
                                )}
                                onValueChange={(value) =>
                                  updateAggregateColumn(agg.id, {
                                    column: value === '__all_columns__' ? '' : value,
                                  })
                                }
                                disabled={columnsDisabled}
                              >
                                <SelectTrigger id={`agg-column-${agg.id}`} className="h-9">
                                  <SelectValue placeholder={columnPlaceholder} />
                                </SelectTrigger>
                                <SelectContent>
                                  {(agg.function === 'COUNT' || agg.function === 'COUNT_DISTINCT') && (
                                    <SelectItem value="__all_columns__">All columns (*)</SelectItem>
                                  )}
                                  {buildOptions(availableColumns, agg.column).map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label htmlFor={`agg-alias-${agg.id}`}>Alias</Label>
                              <Input
                                id={`agg-alias-${agg.id}`}
                                value={agg.column_alias}
                                onChange={(e) => updateAggregateColumn(agg.id, { column_alias: e.target.value })}
                                placeholder="result_name"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* HAVING Conditions */}
      <Card className="p-4">
        <Collapsible open={havingOpen} onOpenChange={setHavingOpen}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-lg font-semibold hover:text-primary transition-colors">
                {havingOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                HAVING Conditions
                {formData.having_conditions.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground">
                    ({formData.having_conditions.length})
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <Button
              onClick={addHavingCondition}
              size="sm"
              variant="outline"
              disabled={!formData.group_by || getAggregateAliases().length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Condition
            </Button>
          </div>

          <CollapsibleContent className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Filter aggregated results after GROUP BY. Requires GROUP BY to be configured with aggregate columns.
            </p>

            {!formData.group_by ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                HAVING requires GROUP BY to be enabled first.
              </div>
            ) : getAggregateAliases().length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                Add aggregate columns in GROUP BY section to use HAVING conditions.
              </div>
            ) : formData.having_conditions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                No HAVING conditions. Click "Add Condition" to filter aggregated results.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.having_conditions.map((condition, index) => (
                  <div key={condition.id} className="border rounded-lg p-4 space-y-3 bg-muted/50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Condition {index + 1}</span>
                      <Button
                        onClick={() => removeHavingCondition(condition.id)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor={`having-alias-${condition.id}`}>Aggregate Alias</Label>
                        <Select
                          value={condition.aggregate_column_alias}
                          onValueChange={(value) =>
                            updateHavingCondition(condition.id, { aggregate_column_alias: value })
                          }
                        >
                          <SelectTrigger id={`having-alias-${condition.id}`}>
                            <SelectValue placeholder="Select aggregate" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAggregateAliases().map((alias) => (
                              <SelectItem key={alias} value={alias}>
                                {alias}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`having-operator-${condition.id}`}>Operator</Label>
                        <Select
                          value={condition.operator}
                          onValueChange={(value) =>
                            updateHavingCondition(condition.id, { operator: value as HavingCondition['operator'] })
                          }
                        >
                          <SelectTrigger id={`having-operator-${condition.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="=">=</SelectItem>
                            <SelectItem value="!=">!=</SelectItem>
                            <SelectItem value=">">&gt;</SelectItem>
                            <SelectItem value="<">&lt;</SelectItem>
                            <SelectItem value=">=">&gt;=</SelectItem>
                            <SelectItem value="<=">&lt;=</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`having-value-${condition.id}`}>Value</Label>
                        <Input
                          id={`having-value-${condition.id}`}
                          type="number"
                          value={condition.value}
                          onChange={(e) => updateHavingCondition(condition.id, { value: Number(e.target.value) })}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {index < formData.having_conditions.length - 1 && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Logic to next:</Label>
                        <Select
                          value={condition.logic || 'AND'}
                          onValueChange={(value) =>
                            updateHavingCondition(condition.id, { logic: value as 'AND' | 'OR' })
                          }
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">AND</SelectItem>
                            <SelectItem value="OR">OR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* JOIN Configuration */}
      <Card className="p-4">
        <Collapsible open={joinsOpen} onOpenChange={setJoinsOpen}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-lg font-semibold hover:text-primary transition-colors">
                {joinsOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                JOIN Operations
                {formData.joins.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground">({formData.joins.length})</span>
                )}
              </button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className="mt-4">
            <JoinBuilder
              joins={formData.joins}
              onUpdate={handleJoinsUpdate}
              currentViewId={currentViewId}
              disabled={false}
              rules={formData.rules}
              ruleLogic={formData.rule_logic}
            />
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Summary */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Transformation Summary</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>
            <strong>WHERE:</strong> {formData.where_conditions.length} condition(s)
          </li>
          <li>
            <strong>GROUP BY:</strong>{' '}
            {formData.group_by
              ? `${formData.group_by.group_by_columns.length} column(s), ${formData.group_by.aggregate_columns.length} aggregate(s)`
              : 'Not configured'}
          </li>
          <li>
            <strong>HAVING:</strong> {formData.having_conditions.length} condition(s)
          </li>
          <li>
            <strong>JOINs:</strong> {formData.joins.length} join(s)
          </li>
        </ul>
      </div>
    </div>
  );
};
